// components/ProfileModal.tsx
import React, { useEffect, useRef, useState } from 'react';
import {
  UserProfile,
  UpdateUserPayload,
  updateUserProfile,
  uploadProfilePicture,
} from '../services/userService';
import {
  ContactSupportPayload,
  sendSupportMessage,
} from '../services/supportService';
import { PLACEHOLDER_IMG_URL, SUPPORT_SUBJECTS } from '../helpers/HelperConstants';
import { Upload } from 'react-feather';


type ModalView = 'view' | 'edit' | 'support' | 'success';

interface ProfileModalProps {
  userId: number;
  profile: UserProfile | null;
  onClose: () => void;
  onProfileUpdated: (updated: UserProfile) => void;
}

function isValidEmail(email: string) : boolean {
    const regex = new RegExp('^[^@]+@[^@]+\.[^@]+');
    const result = regex.exec(email);
    return (result != null);
}

// ── Main modal ───────────────────────────────────────────────
const ProfileModal: React.FC<ProfileModalProps> = ({
  userId,
  profile,
  onClose,
  onProfileUpdated,
}) => {
  const [view, setView] = useState<ModalView>('view');

  // Edit form state
  const [editName, setEditName]   = useState(profile?.name ?? '');
  const [editEmail, setEditEmail] = useState(profile?.email ?? '');
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError]     = useState<string | null>(null);

  // Support form state
  const [supportEmail, setSupportEmail]     = useState(profile?.email ?? '');
  const [supportSubject, setSupportSubject] = useState('');
  const [supportMessage, setSupportMessage] = useState('');
  const [sendLoading, setSendLoading]       = useState(false);
  const [sendError, setSendError]           = useState<string | null>(null);

  // Profile Picture 
  const [editPictureUrl, setEditPictureUrl] = useState(profile?.profilePictureUrl ?? '');
  const [toUpload, setToUpload]             = useState(false);
  const [uploadLoading, setUploadLoading]   = useState(false);
  const fileInputRef                        = useRef<HTMLInputElement>(null);

  // Sync form fields if profile loads after mount
  useEffect(() => {
    if (profile) {
      setEditName(profile.name);
      setEditEmail(profile.email);
      setEditPictureUrl(profile.profilePictureUrl ?? '');
      setSupportEmail(profile.email);
    }
  }, [profile]);

  // Release preview pfp when uploaded.
  useEffect(() => {
    return () => {
      if (editPictureUrl?.startsWith('blob: ')){
        URL.revokeObjectURL(editPictureUrl);
      }
    }

  }, [editPictureUrl]);


  // Edit Handlers
  const handleOpenEdit = () => {
    setSaveError(null);
    setView('edit');
  };

  const handleCancelEdit = () => {
    // Reset to current profile values
    setEditName(profile?.name ?? '');
    setEditEmail(profile?.email ?? '');
    setSaveError(null);
    setView('view');
    setToUpload(false);
  };

  // Profile Picture Handlers
  const handlePfpClick = () => {
    fileInputRef.current?.click();
  };
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;


    setUploadLoading(true);
    setSaveError(null);
    try {
      const localPreviewUrl = URL.createObjectURL(file);
      setEditPictureUrl(localPreviewUrl);
      setToUpload(true)
    } catch(err: any) {
      setSaveError('Failed to upload photo. Please try again later.');
    } finally {
      setUploadLoading(false);
      // Reset input so the same file can be re-selected if needed
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  // Save Handler
  const handleSave = async () => {
    if (!editName.trim()) { setSaveError('Name is required.'); return; }
    if (!editEmail.trim()) { setSaveError('Email is required.'); return; }
    if (!isValidEmail(editEmail)) { setSaveError('Please enter a valid email address.'); return; }

    const payload: UpdateUserPayload = {
      name:  editName.trim(),
      email: editEmail.trim(),
      profilePictureUrl: editPictureUrl || profile?.profilePictureUrl,
    };

    setSaveLoading(true);
    setSaveError(null);
    try {

      if (editPictureUrl?.includes('blob:')){
        const response = await fetch(editPictureUrl);
        const blob = await response.blob();
        const file = new File([blob], 'tempfile', {type: blob.type});

        const url = await uploadProfilePicture(userId, file);
        await setEditPictureUrl(url);
      }

      await updateUserProfile(userId, payload);
      onProfileUpdated({ ...profile!, ...payload });
      setView('view');
    } catch (err: any) {
      if (toUpload) {
        setSaveError("There is an issue with uploading your photo. Please try again later.");
      } else {
        setSaveError("There is an issue updating your information. Please try again later.")
      }
    } finally {
      setSaveLoading(false);
      setToUpload(false);
    }
  };

  // Support Handlers
  const handleOpenSupport = () => {
    setSendError(null);
    setSupportSubject('');
    setSupportMessage('');
    setView('support');
  };

  const handleSendSupport = async () => {
    if (!supportEmail.trim()) { setSendError('Email is required.'); return; }
    if (!isValidEmail(supportEmail)) { setSendError('Please enter a valid email for us to contact you.'); return;}
    if (!supportSubject)      { setSendError('Please select a subject.'); return; }
    if (!supportMessage.trim()) { setSendError('Please enter a message.'); return; }

    const payload: ContactSupportPayload = {
      email:   supportEmail.trim(),
      subject: supportSubject,
      message: supportMessage.trim(),
    };

    setSendLoading(true);
    setSendError(null);
    try {
      await sendSupportMessage(payload);
      setView('success');
    } catch (err: any) {
      setSendError("Failed to send support message. Please try again later.");
    } finally {
      setSendLoading(false);
    }
  };
  
  const picSrc = profile?.profilePictureUrl || PLACEHOLDER_IMG_URL;
  const editPicPreview = editPictureUrl || picSrc;

  return (
    <div className="pf-overlay" onClick={onClose} aria-modal="true" role="dialog">
      <div className="pf-modal" onClick={(e) => e.stopPropagation()}>

        {/* ── VIEW ── */}
        {view === 'view' && (
          <>
            <button className="pf-close" onClick={onClose} aria-label="Close">✕</button>
            <div className="pf-view-body">
              <img
                className="pf-avatar pf-avatar--lg"
                src={picSrc}
                alt="Profile"
                referrerPolicy="no-referrer"
              />
              <p className="pf-name">{profile?.name ?? '—'}</p>
              <p className="pf-email">{profile?.email ?? '—'}</p>
            </div>
            <div className="pf-divider" />
            <div className="pf-actions">
              <button className="ac-btn ac-btn-ghost pf-action-btn" onClick={handleOpenEdit}>
                <i className="ti ti-edit" aria-hidden="true" /> Edit info
              </button>
              <button className="ac-btn ac-btn-ghost pf-action-btn" onClick={handleOpenSupport}>
                <i className="ti ti-headset" aria-hidden="true" /> Contact support
              </button>
            </div>
          </>
        )}

        {/* ── EDIT ── */}
        {view === 'edit' && (
          <>
            <div className="pf-modal-header">
              <h2 className="pf-modal-title">Edit User Information</h2>
              <button className="pf-close pf-close--inline" onClick={onClose} aria-label="Close">✕</button>
            </div>

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none '}}
              onChange={handleFileChange}
            />

            {/* Avatar with camera overlay */}
            <div className="pf-edit-avatar-wrap">
              <div
                className="pf-edit-avatar-inner"
                onClick={handlePfpClick}
                title="Change profile photo"
                role="button"
                aria-label="Change profile photo"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && handlePfpClick()}
              >
                <img
                  className="pf-avatar pf-avatar--xl"
                  src={editPicPreview}
                  alt="Profile"
                  referrerPolicy="no-referrer"
                />
                <div className='pf-avatar-overlay'>
                  <Upload size={16}/>
                </div>
                <div className="pf-avatar-overlay">
                  {uploadLoading
                    ? <div className="spinner" style={{ width: 20, height: 20, borderWidth: 2 }} />
                    : <i className="ti ti-camera" aria-hidden="true" />
                  }
                </div>
              </div>
            </div>
 
            <div className="ac-form" style={{ padding: 0, gap: 14, marginBottom: 20 }}>
              <label className="ac-label">
                Name
                <input
                  className="ac-input"
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Your name"
                />
              </label>
              <label className="ac-label">
                Email
                <input
                  className="ac-input"
                  type="email"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                  placeholder="your@email.com"
                />
              </label>
            </div>
 
            {saveError && <p className="ac-error-msg">{saveError}</p>}
 
            <div className="pf-divider" />
            <div className="pf-actions">
              <button
                className="ac-btn ac-btn-ghost pf-action-btn"
                onClick={handleCancelEdit}
                disabled={saveLoading || uploadLoading}
              >
                Cancel Edit
              </button>
              <button
                className="ac-btn ac-btn-primary pf-action-btn"
                onClick={handleSave}
                disabled={saveLoading || uploadLoading}
              >
                {saveLoading ? 'Saving…' : 'Save Edit'}
              </button>
            </div>
          </>
        )}

        {/* ── SUPPORT ── */}
        {view === 'support' && (
          <>
            <div className="pf-modal-header">
              <h2 className="pf-modal-title">Contact Support</h2>
              <button className="pf-close pf-close--inline" onClick={onClose} aria-label="Close">✕</button>
            </div>

            <div className="ac-form" style={{ padding: 0, gap: 14, marginBottom: 20 }}>
              <label className="ac-label">
                Your email
                <input
                  className="ac-input"
                  type="email"
                  value={supportEmail}
                  onChange={(e) => setSupportEmail(e.target.value)}
                  placeholder="your@email.com"
                />
              </label>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span className="ac-label" style={{ marginBottom: 0 }}>Subject</span>
                <select value={supportSubject} onChange={(e) => setSupportSubject(e.target.value)} className="ac-input">
                  <option value="">Select subject type</option>
                  {SUPPORT_SUBJECTS.map((subject) => (
                    <option key={subject} value={subject}>{subject}</option>
                  ))}
                </select>
              </div>

              <label className="ac-label">
                Message
                <textarea
                  className="ac-input pf-textarea"
                  value={supportMessage}
                  onChange={(e) => setSupportMessage(e.target.value)}
                  placeholder="Describe your issue or question in detail…"
                />
              </label>
            </div>

            {sendError && <p className="ac-error-msg">{sendError}</p>}

            <div className="pf-divider" />
            <div className="pf-actions">
              <button className="ac-btn ac-btn-ghost pf-action-btn" onClick={() => setView('view')} disabled={sendLoading}>
                Cancel
              </button>
              <button className="ac-btn ac-btn-primary pf-action-btn" onClick={handleSendSupport} disabled={sendLoading}>
                {sendLoading ? 'Sending…' : 'Send message'}
              </button>
            </div>
          </>
        )}

        {/* ── SUCCESS ── */}
        {view === 'success' && (
          <div className="pf-success">
            <div className="pf-success-icon">
              <span className="unicode-check"></span>
              <i className="ti ti-check" aria-hidden="true" />
            </div>
            <p className="pf-name">Message sent</p>
            <p className="pf-email" style={{ marginBottom: 24, lineHeight: 1.6 }}>
              We'll get back to you at '{supportEmail}' as soon as possible.
            </p>
            <button className="ac-btn ac-btn-ghost" onClick={onClose}>Done</button>
          </div>
        )}

      </div>
    </div>
  );
};

export default ProfileModal;